import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { 
  Mail, User, Phone, Building, Briefcase, Camera, Save, Edit2, X, 
  Globe, Target, Zap, Receipt, MapPin 
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ProfileAvatarRing } from "@/components/ui/profile-avatar-ring";
import ImageCropModal from "../components/ImageCropModal";
import WhatsAppOnboarding from "./WhatsAppOnboarding";

const MAX_RAW_FILE_SIZE = 10 * 1024 * 1024; // 10MB raw picker ceiling
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function sanitizeUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return "";
  const trimmed = rawUrl.trim();
  if (!trimmed || /^(javascript|data|vbscript):/i.test(trimmed)) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function PlanBadge({ planStatus }) {
  const status = planStatus?.status || "active";
  const name = planStatus?.plan_name || "Free Trial";

  if (status === "expired") {
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border bg-rose-50 text-rose-600 border-rose-200">
        <Zap className="w-3 h-3 mr-1 text-rose-600" />
        {name} (Expired)
      </div>
    );
  }

  if (status === "limit_reached") {
    return (
      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200">
        <Zap className="w-3 h-3 mr-1 text-amber-600" />
        {name} (Limit Reached)
      </div>
    );
  }

  const styles = {
    "Free Trial": "bg-blue-50 text-blue-600 border-blue-200",
    "Event Lite": "bg-blue-50 text-blue-600 border-blue-200",
    "EVENT LITE": "bg-blue-50 text-blue-600 border-blue-200",
    "Event Pro": "bg-purple-50 text-purple-700 border-purple-200",
    "EVENT PRO": "bg-purple-50 text-purple-700 border-purple-200",
  };

  return (
    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${styles[name] || styles["Free Trial"]}`}>
      <Zap className="w-3 h-3 mr-1 text-blue-600" />
      {name}
    </div>
  );
}

export default function Profile() {
  const fileRef = useRef(null);
  const cropObjectUrlRef = useRef(null);
  const previewUrlRef = useRef(null);
  const savedFormRef = useRef(null);
  const { setUser } = useAuth();
  
  const [stats, setStats] = useState({ total: 0, tags: [] });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);

  // Local pending avatar state (Prevents out-of-sync cancellations)
  const [pendingAvatarBlob, setPendingAvatarBlob] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    designation: "",
    address: "",
    gst_number: "",
    avatar_base64: null,
    specialization: "",
    website: "",
    company_email: "",
    company_phone: "",
    plan_status: {
      plan_name: "Event Lite",
      status: "active",
      scans_used: 0,
      scan_limit: 350,
      is_expired: false,
      is_limit_reached: false,
    }
  });

  const loadUserData = useCallback(async (signal) => {
    try {
      const [authRes, statsRes] = await Promise.all([
        api.get("/auth/me", { signal }),
        api.get("/leads/stats/summary", { signal }).catch(() => ({ data: null }))
      ]);

      if (authRes?.data) {
        const d = authRes.data;
        const liveStats = statsRes?.data;

        const currentScansUsed = liveStats?.scans_used !== undefined
          ? Number(liveStats.scans_used)
          : Number(d.plan_status?.scans_used ?? d.scans_used ?? 0);

        const currentScanLimit = liveStats?.scan_limit !== undefined
          ? Number(liveStats.scan_limit)
          : Number(d.plan_status?.scan_limit ?? d.scan_limit ?? 350);

        const currentPlanName = liveStats?.plan_name 
          || liveStats?.plan 
          || d.plan_status?.plan_name 
          || d.plan_name 
          || "Event Lite";

        const isExpired = liveStats?.expires_at 
          ? new Date(liveStats.expires_at) < new Date() 
          : Boolean(d.plan_status?.is_expired);

        const isLimitReached = currentScansUsed >= currentScanLimit;

        const computedStatus = isExpired 
          ? "expired" 
          : (isLimitReached ? "limit_reached" : (liveStats?.is_active ? "active" : (d.plan_status?.status || "active")));

        const loadedForm = {
          name: d.name || "",
          email: d.email || "",
          phone: d.phone || "",
          company_name: d.company_name || "",
          designation: d.designation || "",
          address: d.address || "",
          gst_number: d.gst_number || "",
          avatar_base64: d.avatar_base64 || null,
          specialization: d.specialization || "",
          website: d.website || "",
          company_email: d.company_email || "",
          company_phone: d.company_phone || "",
          plan_status: {
            ...(d.plan_status || {}),
            plan_name: currentPlanName,
            status: computedStatus,
            scans_used: currentScansUsed,
            scan_limit: currentScanLimit,
            is_expired: isExpired,
            is_limit_reached: isLimitReached,
          },
        };

        setForm(loadedForm);
        savedFormRef.current = loadedForm;

        if (liveStats) {
          setStats(liveStats);
        }

        // Clean user object before saving to localStorage to prevent quota exhaustion
        const storageSafeUser = { ...d, scans_used: currentScansUsed, scan_limit: currentScanLimit, plan_name: currentPlanName };
        if (typeof storageSafeUser.avatar_base64 === "string" && storageSafeUser.avatar_base64.length > 50000) {
          delete storageSafeUser.avatar_base64;
        }

        setUser(d);
        try {
          localStorage.setItem("ls_user", JSON.stringify(storageSafeUser));
        } catch (e) {
          console.warn("Storage quota exceeded, caching essential user attributes only.", e);
        }
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      toast.error(err?.response?.data?.detail || "Could not load profile details");
    }
  }, [setUser]);

  useEffect(() => {
    const controller = new AbortController();
    loadUserData(controller.signal);
    return () => controller.abort();
  }, [loadUserData]);

  // Clean memory on unmount
  useEffect(() => {
    return () => {
      if (cropObjectUrlRef.current) URL.revokeObjectURL(cropObjectUrlRef.current);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const handleStartEdit = () => {
    savedFormRef.current = { ...form };
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPendingAvatarBlob(null);
    setAvatarPreviewUrl(null);

    if (savedFormRef.current) {
      setForm(savedFormRef.current);
    }
    setIsEditing(false);
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    // Strict validation: both MIME and extension must match
    const validMimes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const isMimeValid = validMimes.includes(file.type.toLowerCase());
    const isExtValid = /\.(jpe?g|png|webp)$/i.test(file.name);

    if (!isMimeValid || !isExtValid) {
      toast.error("Invalid image format. Please select a PNG, JPG, or WEBP file.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    if (file.size > MAX_RAW_FILE_SIZE) {
      toast.error("Selected image exceeds 10MB limit.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(file);
    cropObjectUrlRef.current = objectUrl;
    setCropImageSrc(objectUrl);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Local-only crop capture (No server request until user explicitly clicks Save)
  const handleApplyCroppedBlob = (croppedBlob) => {
    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
      cropObjectUrlRef.current = null;
    }
    setCropImageSrc(null);

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    const previewUrl = URL.createObjectURL(croppedBlob);
    previewUrlRef.current = previewUrl;

    setPendingAvatarBlob(croppedBlob);
    setAvatarPreviewUrl(previewUrl);
  };

  const handleCancelCrop = () => {
    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
      cropObjectUrlRef.current = null;
    }
    setCropImageSrc(null);
  };

  // Unified save transaction
  const handleSave = async (e) => {
    e.preventDefault();

    if (form.phone && form.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Personal mobile number must be 10 digits.");
      return; 
    }

    if (form.company_email && !EMAIL_REGEX.test(form.company_email.trim())) {
      toast.error("Please enter a valid company email address.");
      return;
    }

    if (form.gst_number) {
      const cleanGst = form.gst_number.trim().toUpperCase();
      if (!GSTIN_REGEX.test(cleanGst)) {
        toast.error("Invalid GSTIN format. Example: 24AAACC4175P1Z1");
        return;
      }
    }

    setSaving(true);
    let updatedAvatarBase64 = form.avatar_base64;

    try {
      // 1. Upload pending avatar if a new crop exists
      if (pendingAvatarBlob) {
        const formData = new FormData();
        formData.append("file", pendingAvatarBlob, "avatar.webp");

        // Omit manual Content-Type header so the browser automatically sets boundary
        const avatarRes = await api.post("/auth/avatar", formData);
        if (avatarRes.data?.avatar_base64) {
          updatedAvatarBase64 = avatarRes.data.avatar_base64;
        }

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
        setPendingAvatarBlob(null);
        setAvatarPreviewUrl(null);
      }

      // 2. Commit profile fields (Omits plan/quota authorization keys)
      const payload = {
        name: form.name.trim(),
        phone: form.phone ? form.phone.trim() : null,
        company_name: form.company_name ? form.company_name.trim() : null,
        designation: form.designation ? form.designation.trim() : null,
        address: form.address ? form.address.trim() : null,
        gst_number: form.gst_number ? form.gst_number.trim().toUpperCase() : null,
        specialization: form.specialization ? form.specialization.trim() : null,
        website: form.website ? form.website.trim() : null,
        company_email: form.company_email ? form.company_email.trim() : null,
        company_phone: form.company_phone ? form.company_phone.trim() : null,
      };

      const res = await api.patch("/auth/me", payload);
      
      const nextUser = {
        ...(res?.data || form),
        avatar_base64: updatedAvatarBase64
      };

      setUser(nextUser);
      setIsEditing(false);
      toast.success("Profile saved successfully!");
      await loadUserData();

    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const safeWebsiteUrl = sanitizeUrl(form.website);
  const activeAvatarDisplay = avatarPreviewUrl || form.avatar_base64;

  return (
    <div className="px-5 pt-8 pb-32 max-w-md mx-auto font-sans" data-testid="profile-page">
      {cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          onCancel={handleCancelCrop}
          onApply={handleApplyCroppedBlob}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Account</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-zinc-950">Profile</h1>
        </div>
        {!isEditing ? (
          <button 
            type="button"
            onClick={handleStartEdit} 
            className="p-2.5 bg-zinc-100 text-zinc-700 rounded-full hover:bg-zinc-200 transition cursor-pointer"
            title="Edit Profile"
          >
            <Edit2 className="h-4 w-4" />
          </button>
        ) : (
          <button 
            type="button"
            onClick={handleCancelEdit} 
            className="p-2.5 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition cursor-pointer"
            title="Cancel Editing"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSave} className="p-5 border border-zinc-200 rounded-3xl bg-white mb-6 shadow-xs">
          <div className="flex flex-col items-center mb-6">
            <div 
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && fileRef.current?.click()}
              className="relative h-24 w-24 rounded-full bg-zinc-100 border-2 border-zinc-200 flex items-center justify-center overflow-hidden cursor-pointer group shadow-2xs" 
              onClick={() => fileRef.current?.click()}
              title="Click to crop and upload photo"
            >
              {activeAvatarDisplay ? (
                <img 
                  src={activeAvatarDisplay} 
                  alt="Profile Preview" 
                  className="h-full w-full object-cover" 
                />
              ) : (
                <User className="h-8 w-8 text-zinc-400" />
              )}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-6 w-6 text-white" />
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-medium">
              {pendingAvatarBlob ? "Pending save (Tap to change)" : "Tap to update photo"}
            </p>

            <input 
              ref={fileRef} 
              type="file" 
              accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" 
              className="hidden" 
              onChange={(e) => handleFileSelect(e.target.files?.[0])} 
            />
          </div>

          <div className="flex flex-col gap-4">
            <Field icon={<User className="h-4 w-4 text-zinc-400" />} label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field icon={<Mail className="h-4 w-4 text-zinc-400" />} label="Login Email Address" type="email" value={form.email} disabled={true} />
            <Field icon={<Phone className="h-4 w-4 text-zinc-400" />} label="Personal Mobile Number" type="tel" maxLength={10} value={form.phone} onChange={(v) => setForm({ ...form, phone: v.replace(/\D/g, "").slice(0, 10) })} />
            <Field icon={<Building className="h-4 w-4 text-zinc-400" />} label="Company Name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
            <Field icon={<Briefcase className="h-4 w-4 text-zinc-400" />} label="Designation" value={form.designation} onChange={(v) => setForm({ ...form, designation: v })} />
            <Field icon={<MapPin className="h-4 w-4 text-zinc-400" />} label="Billing / Office Address" placeholder="Company Office Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <Field icon={<Receipt className="h-4 w-4 text-zinc-400" />} label="Buyer GSTIN" placeholder="24AAACC4175P1Z1" value={form.gst_number} onChange={(v) => setForm({ ...form, gst_number: v.toUpperCase() })} />

            <div className="mt-4 pt-4 border-t border-zinc-200">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3">📱 WhatsApp Brochure Card Profile</p>
              <div className="flex flex-col gap-4">
                <Field icon={<Target className="h-4 w-4 text-zinc-400" />} label="Specialization" placeholder="Core Domain" value={form.specialization} onChange={(v) => setForm({ ...form, specialization: v })} />
                <Field icon={<Globe className="h-4 w-4 text-zinc-400" />} label="Website URL" placeholder="https://example.com" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
                <Field icon={<Mail className="h-4 w-4 text-zinc-400" />} label="Public Business Email" placeholder="contact@example.com" value={form.company_email} onChange={(v) => setForm({ ...form, company_email: v })} />
                <Field icon={<Phone className="h-4 w-4 text-zinc-400" />} label="Public Business Phone" placeholder="Official Contact Number" value={form.company_phone} onChange={(v) => setForm({ ...form, company_phone: v })} />
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={saving} 
            className="mt-6 h-12 w-full bg-blue-600 text-white text-sm font-bold rounded-2xl flex items-center justify-center gap-2 hover:bg-blue-700 transition cursor-pointer disabled:opacity-60 shadow-xs"
          >
            {saving ? "Saving..." : <><Save className="h-4 w-4" /> Save Changes</>}
          </button>
        </form>
      ) : (
        <div className="p-5 border border-zinc-200/90 rounded-3xl bg-white mb-6 shadow-xs space-y-4">
          <div className="flex items-center gap-4">
            <ProfileAvatarRing user={form} size={72} />

            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <p className="font-display text-xl font-bold text-zinc-950">{form.name || "User"}</p>
                <PlanBadge planStatus={form.plan_status} />
              </div>
              <p className="text-xs text-zinc-500 flex items-center gap-1.5 font-medium">
                <Mail className="h-3.5 w-3.5 text-zinc-400" /> {form.email}
              </p>
              <p className="text-xs text-zinc-700 font-semibold pt-0.5">
                Card Scan Limit:{" "}
                <span className="font-bold text-zinc-950">
                  <span>{form.plan_status?.scans_used ?? 0}</span>
                  <span className="text-zinc-400 mx-1 font-normal">/</span>
                  {form.plan_status?.is_expired ? (
                    <span className="line-through text-zinc-400 decoration-zinc-400 decoration-[1.5px] font-medium">
                      {form.plan_status?.scan_limit}
                    </span>
                  ) : (
                    <span>{form.plan_status?.scan_limit}</span>
                  )}
                  {form.plan_status?.is_expired ? (
                    <span className="text-rose-600 ml-1.5 font-bold text-[11px]">(Plan Expired)</span>
                  ) : form.plan_status?.is_limit_reached ? (
                    <span className="text-amber-600 ml-1.5 font-bold text-[11px]">(Limit Reached)</span>
                  ) : null}
                </span>
              </p>
            </div>
          </div>

          {(form.company_name || form.designation || form.phone || form.address || form.gst_number) && (
            <div className="pt-2 border-t border-zinc-100 flex flex-col gap-2 text-xs text-zinc-600">
              {form.company_name && (
                <p className="flex items-center gap-2 font-medium">
                  <Briefcase className="h-4 w-4 text-zinc-400 shrink-0" />
                  <span>{form.designation ? `${form.designation} at ` : ""}<strong className="text-zinc-900">{form.company_name}</strong></span>
                </p>
              )}
              {form.phone && (
                <p className="flex items-center gap-2 font-medium">
                  <Phone className="h-4 w-4 text-zinc-400 shrink-0" />
                  <span>{form.phone}</span>
                </p>
              )}
              {form.address && (
                <p className="flex items-start gap-2 font-medium">
                  <MapPin className="h-4 w-4 text-zinc-400 shrink-0 mt-0.5" />
                  <span className="leading-snug text-zinc-700">{form.address}</span>
                </p>
              )}
              {form.gst_number && (
                <p className="flex items-center gap-2 text-blue-700 font-mono font-bold">
                  <Receipt className="h-4 w-4 text-blue-600 shrink-0" /> GSTIN: {form.gst_number}
                </p>
              )}
            </div>
          )}

          <div className="border-l-4 border-blue-600 bg-blue-50/40 rounded-2xl p-4 space-y-2 text-xs text-zinc-700">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-blue-700">
              Active WhatsApp Intro Profile
            </p>
            <div className="space-y-1.5 pt-1">
              <p className="flex items-center gap-2">
                <Target className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span className="font-semibold text-zinc-900">{form.specialization || "Not Configured"}</span>
              </p>
              <p className="flex items-center gap-2">
                <Globe className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                {safeWebsiteUrl ? (
                  <a 
                    href={safeWebsiteUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 underline font-medium hover:text-blue-800 break-all"
                  >
                    {form.website}
                  </a>
                ) : (
                  <span className="text-zinc-400 font-medium">Not Configured</span>
                )}
              </p>
              <p className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span>{form.company_email || "Not Configured"}</span>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span>{form.company_phone || "Not Configured"}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <WhatsAppOnboarding />
      </div>

      <div className="p-5 border border-zinc-200/90 rounded-3xl bg-white shadow-xs">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1">
          Pipeline summary
        </p>
        
        <p className="font-display text-4xl font-black text-zinc-950" data-testid="profile-total-leads">
          {stats?.total ?? 0}
        </p>
        <p className="text-xs text-zinc-500 font-medium mt-0.5">
          total leads captured
        </p>
        
        <div className="mt-4 flex flex-wrap gap-2">
          {stats?.tags?.length > 0 ? (
            stats.tags.map((t) => (
              <span 
                key={t.tag} 
                className="px-3 py-1 rounded-full text-xs font-bold bg-zinc-100 text-zinc-700 border border-zinc-200"
              >
                {t.tag} · {t.count}
              </span>
            ))
          ) : (
            <p className="text-xs text-zinc-400 font-medium italic">
              No tags recorded yet
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon, label, value, onChange, type = "text", disabled = false, maxLength, placeholder }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-zinc-700">{label}</label>
      <div className="relative">
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">{icon}</div>
        <input
          type={type}
          inputMode={type === "tel" ? "numeric" : "text"}
          maxLength={maxLength}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange && onChange(e.target.value)}
          disabled={disabled}
          className={`w-full pl-10 pr-3.5 py-2.5 border border-zinc-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition ${
            disabled ? "bg-zinc-100 text-zinc-500 cursor-not-allowed" : "bg-zinc-50 text-zinc-900"
          }`}
        />
      </div>
    </div>
  );
}